import React, { useEffect, useState } from 'react';
import { Image, Button, Dropdown } from 'react-bootstrap';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { S3_URL, USER_TYPES } from 'constants/index';
import { decreaseFollowing, increaseFollowing, unitNumber, withAuth } from 'utils';
import './styles.scss';
import useAuthStore, { updateUserInfo } from 'store/auth';
import {
	useAcceptConnectionRequestMutation,
	useAddToTalentMutation,
	useBlockUserMutation,
	useFollowCompanyMutation,
	useRemoveConnectRequestMutation,
	useReportMutation,
	useSendConnectRequestMutation,
	useUnblockUserMutation,
} from 'services';
import { useNavigate, useParams } from 'react-router-dom';
import useCommonStore, { setModalConfig } from 'store/common';
import toast from 'react-hot-toast';
import ChatBtn from 'components/Common/ActionButton/ChatBtn';
import { removeFeedByUserId } from 'store/feeds';
import useTranslate from 'hooks/useTranslate';
import SaveTalentBtn from 'components/Common/ActionButton/SaveTalent';
import { useQueryClient } from '@tanstack/react-query';

const OverviewBox: React.FC<any> = ({
	data,
	onShowModal,
	isSelf,
	companyDetails = {},
	receiverId,
}) => {
	const { translate } = useTranslate();
	const { user, token }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const [isConnected, setIsConnected] = useState(0);
	const [connectRequestSentBySelf, setConnectRequestSentBySelf] = useState(false);
	const [isBlock, setIsBlock] = useState(data?.companyDetails?.isBlocked);
	const [followStatus, setFollowStatus] = useState(companyDetails?.isCompanyFollow || false);
	const [isFavourite, setIsFavourite] = useState<boolean>(data?.isSaved || false);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { mutateAsync: sendRequestMutation } = useSendConnectRequestMutation();
	const { mutateAsync: removeConnectionMutation } = useRemoveConnectRequestMutation();
	const { mutateAsync: blockUserMutation } = useBlockUserMutation();
	const { mutateAsync: unBlockUserMutation } = useUnblockUserMutation();
	const { mutateAsync: followCompanyMutation } = useFollowCompanyMutation();
	const { mutateAsync: reportProfileMutation } = useReportMutation();
	const { mutateAsync: acceptRequestMutation } = useAcceptConnectionRequestMutation();
	const { mutateAsync: rejectRequestMutation } = useRemoveConnectRequestMutation();
	const { mutateAsync: addToFavMutation } = useAddToTalentMutation();
	const { id: contentId } = useParams();
	const [followersCount, setFollowersCount] = useState(data?.followers);
	const [buttonText, setButtonText] = useState('');

	const urlParams = useParams();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	useEffect(() => {
		if (data) {
			setIsConnected(data?.connectStatus);
			setConnectRequestSentBySelf(data?.sendedBy || false);
		}
	}, [data]);

	useEffect(() => {
		let btnText = '';
		if (isConnected === 2 && connectRequestSentBySelf) btnText = translate('Button.requested');
		else if (isConnected === 2) btnText = translate('Button.accept');
		else if (isConnected === 1) btnText = translate('Button.connected');
		else btnText = translate('Button.connect');
		setButtonText(btnText);
	}, [isConnected, connectRequestSentBySelf]);

	const reportProfile = () => {
		setModalConfig({
			visible: true,
			id: contentId,
			onClick: reportProfileFunction,
			type: 'reportFeeds',
			contentType: 'profile',
		});
	};

	const reportProfileFunction = (values: any) => {
		reportProfileMutation({ ...values, contentId: Number(values?.contentId) }).then((res: any) => {
			if (res?.success) {
				toast(translate(`Profile.profileReported`, { field: translate('Report.profile') }));
			}
			hideCommonModal();
		});
	};

	const unFollowProfile = () => {
		setFollowersCount((prev: any) => prev - 1);
		decreaseFollowing(user?.followings);
		followCompanyMutation({ id: companyDetails?.id })
			.then((res: any) => {
				if (res?.success) {
					setFollowStatus((prev: any) => !prev);
				} else {
					increaseFollowing(user?.followings);
					hideCommonModal();
				}
			})
			.catch(() => {
				increaseFollowing(user?.followings);
				setFollowersCount((prev: any) => prev + 1);
			})
			.finally(() => {
				hideCommonModal();
			});
	};

	const followCompany = () => {
		if (followStatus) {
			setModalConfig({
				visible: true,
				type: 'confirmModal',
				onClick: unFollowProfile,
				data: {
					title: translate('Modal.wantToUnfollow', { title: `${translate('Modal.company')}` }),
				},
				props: {
					dialogClassName: 'confirm-modal',
				},
			});
		} else {
			setFollowersCount((prev: any) => prev + 1);
			increaseFollowing(user?.followings);
			followCompanyMutation({ id: companyDetails?.id })
				.then((res: any) => {
					if (res?.success) {
						setFollowStatus((prev: any) => !prev);
					} else {
						decreaseFollowing(user?.followings);
					}
				})
				.catch(() => {
					setFollowersCount((prev: any) => prev - 1);
					decreaseFollowing(user?.followings);
				});
		}
	};

	const handleConnectRequest = () => {
		if (data?.userType === USER_TYPES.SEEKER) {
			if (isConnected === 0) {
				sendConnectionRequest();
			} else if (isConnected === 2 && connectRequestSentBySelf) {
				removeConnectionModal();
			} else if (isConnected === 2) {
				handleAcceptRequest();
			}
		}
	};

	const sendConnectionRequest = () => {
		sendRequestMutation({ id: Number(urlParams?.id) }).then((res: any) => {
			if (res?.success) {
				setIsConnected(2);
				setConnectRequestSentBySelf(true);
			}
		});
	};

	const removeConnectionModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: removeConnection,
			data: {
				title: translate('Modal.wantToCancelRequest'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const goToConnectionList = () => {
		if (isSelf) {
			if (data?.userType === USER_TYPES.COMPANY) {
				navigate(`/company/followers-list?activeTab=followers&userId=${urlParams?.id || ''}`);
			} else {
				navigate(`/seeker/my-connections?activeTab=connected&userId=${urlParams?.id || ''}`);
			}
		} else {
			setModalConfig({
				visible: true,
				id: receiverId,
				type: data?.userType === USER_TYPES.COMPANY ? 'followersList' : 'connectionsList',
				onClose: () => {
					setModalConfig({
						visible: false,
					});
				},
				props: {
					size: 'lg',
					dialogClassName: 'dialog-like-list',
				},
			});
		}
	};

	const goToFollowingList = () => {
		if (isSelf) {
			navigate(
				`/${data?.userType}/followings-list?activeTab=company&/company/followers-list?activeTab=followers&userId=${urlParams?.id || ''}`,
			);
		} else {
			setModalConfig({
				visible: true,
				id: receiverId,
				type: 'followingList',
				onClose: () => {
					setModalConfig({
						visible: false,
					});
				},
				props: {
					size: 'lg',
					dialogClassName: 'dialog-like-list',
				},
			});
		}
	};

	const goToEmployeeList = () => {
		navigate(
			`/company/followers-list?activeTab=employees&/company/followers-list?activeTab=followers&userId=${urlParams?.id || ''}`,
		);
	};

	const handleAcceptRequest = () => {
		acceptRequestMutation(Number(urlParams?.id)).then((_res: any) => {
			if (_res?.success) {
				updateUserInfo({ connection: user?.connection + 1 });
				setIsConnected(1);
				setConnectRequestSentBySelf(false);
			}
		});
	};

	const handleRejectRequest = () => {
		rejectRequestMutation(Number(urlParams?.id)).then((_res: any) => {
			if (_res?.success) {
				setIsConnected(0);
				setConnectRequestSentBySelf(false);
			}
		});
	};

	const removeConnection = () => {
		removeConnectionMutation(urlParams?.id).then((res: any) => {
			if (res?.success) {
				setIsConnected(0);
				setConnectRequestSentBySelf(false);
				setModalConfig({
					visible: false,
				});
			}
		});
	};

	const blockProfile = () => {
		if ([USER_TYPES.COMPANY].includes(user.userType)) {
			updateUserInfo({ followers: user?.followers - 1 });
		} else {
			updateUserInfo({ connection: user?.connection - 1 });
		}
		setIsBlock(true);

		blockUserMutation(Number(urlParams?.id))
			.then((res: any) => {
				if (res?.success) {
					removeFeedByUserId(Number(urlParams?.id));
					setIsConnected(0);
					setConnectRequestSentBySelf(false);
					setFollowStatus(false);
				} else {
					if ([USER_TYPES.COMPANY].includes(user.userType)) {
						updateUserInfo({ followers: user?.followers + 1 });
					} else {
						updateUserInfo({ connection: user?.connection - 1 });
					}
					setIsBlock(false);
					setIsConnected(1);
				}
			})
			.catch((_err: any) => {
				setIsBlock(false);
				setIsConnected(1);
			})
			.finally(() => {
				setModalConfig({ visible: false });
			});
	};

	const handleBlockUserModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: blockProfile,
			data: {
				title: translate('Modal.wantToBlockAccount'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const handleUnblockUser = () => {
		setIsBlock(false);
		unBlockUserMutation(Number(urlParams?.id))
			.then((res: any) => {
				if (res?.success) {
					//
				} else {
					setIsBlock(true);
				}
			})
			.catch((_err: any) => {
				setIsBlock(true);
			});
	};

	const addToFavorite = () => {
		setIsFavourite((prev) => !prev);
		addToFavMutation({ userId: data.userId })
			.then((_res: any) => {
				queryClient.invalidateQueries({ queryKey: ['get-saved-talent'] });
			})
			.catch(() => {
				setIsFavourite((prev) => !prev);
			});
	};

	return (
		<>
			<div className="overview-box position-relative d-flex align-items-end">
				<div className="overview-box-bg w-100 h-100 position-absolute left-0 top-0">
					<Image
						src={data?.coverPicture ? `${S3_URL}${data.coverPicture}` : IMAGE_PATHS.UserBG}
						alt="bg"
						className="w-100 h-100 object-fit-cover"
						loading="lazy"
					/>
				</div>
				<div className="overview-box-overlay w-100 h-100 position-absolute left-0 top-0"></div>
				{isSelf && (
					<Button
						onClick={() => onShowModal('addCoverImage')}
						className="edit-btn rounded-circle p-0 border-0 position-absolute update-img"
					>
						<i className="ri-camera-line"></i>
					</Button>
				)}
				<div className="d-none d-md-block w-100">
					<div className="overview-box-inner d-flex flex-column flex-md-row w-100 gap-3 gap-md-4 align-items-md-center">
						<div className="company-profile-img position-relative">
							<Image
								src={
									data?.profilePicture ? `${S3_URL}${data.profilePicture}` : IMAGE_PATHS.UserDefault
								}
								alt="img"
								className="w-100 h-100 object-fit-cover"
								loading="lazy"
							/>
							{isSelf && (
								<Button
									className="edit-btn rounded-circle p-0 border-0 bg-white position-absolute update-profile"
									onClick={() => onShowModal('addProfileImage')}
								>
									<i className="ri-camera-line"></i>
								</Button>
							)}
						</div>
						<div className="overview-box-inner-details flex-1 d-flex flex-column">
							<div className="company-info-box d-flex justify-content-between gap-2">
								<div className="company-info flex-1">
									<div className="d-flex align-items-center gap-3 mb-1 mb-xl-2">
										<h5 className="company-info-name text-white fw-bold mb-0">{`${data?.firstName ?? ''} ${data?.lastName ?? ''}`}</h5>
										{isSelf && (
											<Button
												className="edit-btn rounded-circle p-0 border-0 text-white"
												onClick={() =>
													onShowModal(
														user?.userType === USER_TYPES.COMPANY
															? 'editCompanyDetails'
															: 'editPersonalDetails',
													)
												}
											>
												<i className="ri-edit-line"></i>
											</Button>
										)}
									</div>
									<p className="company-info-about text-white mb-1 mb-xl-2">{data?.designation}</p>
									<p className="company-info-about text-white mb-1 mb-xl-2">{data?.address}</p>
									<div className="d-flex gap-1 gap-lg-2">
										{!Number.isInteger(data?.employees) ? (
											<p
												className="company-info-about text-white mb-0"
												onClick={withAuth(token, goToConnectionList)}
											>
												<span>{unitNumber(data?.connection, 0)}</span>{' '}
												{translate('Common.connection')}
											</p>
										) : (
											<p
												className="company-info-about text-white mb-0"
												onClick={withAuth(token, goToConnectionList)}
											>
												<span>{unitNumber(followersCount, 0)}</span> {translate('Common.followers')}
											</p>
										)}

										<p className="company-info-about text-white mb-0">•</p>
										<p
											className="company-info-about text-white mb-0"
											onClick={withAuth(token, goToFollowingList)}
										>
											<span>{unitNumber(data?.followings, 0)}</span>{' '}
											{translate('Sidebar.followings')}
										</p>
										{Number.isInteger(data?.employees) && (
											<>
												<p className="company-info-about text-white mb-0">•</p>
												<p
													className="company-info-about text-white mb-0"
													onClick={withAuth(token, goToEmployeeList)}
												>
													<span>{unitNumber(data?.employees, 0)}</span>{' '}
													{translate('Common.employees')}
												</p>
											</>
										)}
									</div>
								</div>
								<div className="company-action d-flex flex-column flex-sm-row align-items-end align-items-sm-start gap-2 gap-lg-3">
									{!isSelf && (
										<>
											{!isBlock &&
												((user?.userType === USER_TYPES.COMPANY &&
													data?.userType === USER_TYPES.SEEKER) ||
													(user?.userType === USER_TYPES.SEEKER &&
														data?.userType === USER_TYPES.SEEKER &&
														isConnected === 1) ||
													(user?.userType === USER_TYPES.SEEKER &&
														data?.userType === USER_TYPES.COMPANY &&
														data?.isAlreadyMessage)) && (
													<ChatBtn mode="sendMsg" receiverId={receiverId} />
												)}

											{!isBlock &&
												user?.userType === USER_TYPES.COMPANY &&
												data?.userType === USER_TYPES.SEEKER && (
													<SaveTalentBtn
														isFavourite={isFavourite}
														handleClick={(e: any) => {
															e.stopPropagation();
															addToFavorite();
														}}
													/>
												)}

											{user?.userType === USER_TYPES.COMPANY &&
											data?.userType === USER_TYPES.SEEKER ? (
												<></>
											) : (
												<>
													<div>
														<Button
															disabled={isBlock}
															onClick={
																user?.userType === USER_TYPES.SEEKER &&
																data?.userType === USER_TYPES.SEEKER
																	? withAuth(token, handleConnectRequest)
																	: withAuth(token, followCompany)
															}
															className="filled-btn rounded-pill lh-1"
														>
															{user?.userType === USER_TYPES.SEEKER &&
															data?.userType === USER_TYPES.SEEKER
																? buttonText
																: !followStatus
																	? translate('Common.follow')
																	: translate('Common.unFollow')}
														</Button>
													</div>
													{user?.userType === USER_TYPES.SEEKER &&
														data?.userType === USER_TYPES.SEEKER &&
														isConnected === 2 &&
														!connectRequestSentBySelf && (
															<div>
																<Button
																	disabled={isBlock}
																	onClick={withAuth(token, handleRejectRequest)}
																	className="filled-btn rounded-pill lh-1"
																>
																	{translate('Button.reject')}
																</Button>
															</div>
														)}
												</>
											)}
											<div className="edit-action">
												<Dropdown>
													<Dropdown.Toggle
														variant="success"
														id="dropdown-basic"
														className="p-0 border-0 rounded-circle"
													>
														<i className="ri-more-2-fill"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														{isConnected == 1 && (
															<Dropdown.Item
																onClick={withAuth(token, () =>
																	setModalConfig({
																		visible: true,
																		type: 'confirmModal',
																		id: urlParams?.id,
																		onClick: removeConnection,
																		data: {
																			title: translate('Modal.wantToRemoveConnection'),
																		},
																		props: {
																			dialogClassName: 'confirm-modal',
																		},
																	}),
																)}
															>
																<i className="ri-user-minus-line"></i>{' '}
																{translate('Report.removeConnection')}
															</Dropdown.Item>
														)}
														<Dropdown.Item onClick={withAuth(token, reportProfile)}>
															<i className="ri-flag-line"></i> {translate('Report.report')}
														</Dropdown.Item>
														{isBlock ? (
															<Dropdown.Item onClick={withAuth(token, handleUnblockUser)}>
																<i className="ri-spam-2-line"></i> {translate('Report.unblock')}
															</Dropdown.Item>
														) : (
															<Dropdown.Item onClick={withAuth(token, handleBlockUserModal)}>
																<i className="ri-spam-2-line"></i> {translate('Report.block')}
															</Dropdown.Item>
														)}
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="overview-box-mobile-view d-md-none">
				<div className="overview-box-inner d-flex flex-column flex-md-row w-100 gap-2 gap-md-4 align-items-md-center">
					<div className="company-profile-img position-relative">
						<Image
							src={
								data?.profilePicture ? `${S3_URL}${data.profilePicture}` : IMAGE_PATHS.UserDefault
							}
							alt="img"
							className="w-100 h-100 object-fit-cover"
							loading="lazy"
						/>
						{isSelf && (
							<Button
								className="edit-btn rounded-circle p-0 border-0 bg-white position-absolute update-profile"
								onClick={() => onShowModal('addProfileImage')}
							>
								<i className="ri-camera-line"></i>
							</Button>
						)}
					</div>
					<div className="overview-box-inner-details flex-1 d-flex flex-column">
						<div className="company-info-box d-flex flex-column flex-md-row justify-content-between gap-2">
							<div className="company-info flex-1">
								<div className="d-flex align-items-center justify-content-between mb-1 mb-xl-2">
									<h5 className="company-info-name fw-bold mb-0">{`${data?.firstName ?? ''} ${data?.lastName ?? ''}`}</h5>
									{isSelf && (
										<Button
											className="edit-btn rounded-circle p-0 border-0"
											onClick={() =>
												onShowModal(
													user?.userType === USER_TYPES.COMPANY
														? 'editCompanyDetails'
														: 'editPersonalDetails',
												)
											}
										>
											<i className="ri-edit-line"></i>
										</Button>
									)}
								</div>
								<p className="company-info-about mb-1 mb-xl-2">{data?.designation}</p>
								<p className="company-info-about mb-1 mb-xl-2">{data?.address}</p>
								<div className="d-flex gap-1 gap-lg-2">
									{user?.userType === USER_TYPES.SEEKER ? (
										<p className="company-info-about mb-0">
											<span>{unitNumber(data?.connection, 0)}</span>{' '}
											{translate('Common.connection')}
										</p>
									) : (
										<p className="company-info-about mb-0">
											<span>{unitNumber(data?.followers, 0)}</span> {translate('Common.followers')}
										</p>
									)}
									<p className="company-info-about mb-0">•</p>
									<p className="company-info-about mb-0">
										<span>{unitNumber(data?.followings, 0)}</span> {translate('Sidebar.followings')}
									</p>
									{Number.isInteger(data?.employees) && (
										<>
											<p className="company-info-about mb-0">•</p>
											<p className="company-info-about mb-0">
												<span>{unitNumber(data?.employees, 0)}</span>{' '}
												{translate('Common.employees')}
											</p>
										</>
									)}
								</div>
							</div>
							<div className="company-action d-flex align-items-start gap-2 gap-lg-3">
								{!isSelf && (
									<>
										{!isBlock &&
											((user?.userType === USER_TYPES.COMPANY &&
												data?.userType === USER_TYPES.SEEKER) ||
												(user?.userType === USER_TYPES.SEEKER &&
													data?.userType === USER_TYPES.SEEKER &&
													isConnected === 1) ||
												(user?.userType === USER_TYPES.SEEKER &&
													data?.userType === USER_TYPES.COMPANY &&
													data?.isAlreadyMessage)) && (
												<ChatBtn mode="sendMsg" receiverId={Number(receiverId)} />
											)}

										{!isBlock &&
											user?.userType === USER_TYPES.COMPANY &&
											data?.userType === USER_TYPES.SEEKER && (
												<SaveTalentBtn
													isFavourite={isFavourite}
													handleClick={(e: any) => {
														e.stopPropagation();
														addToFavorite();
													}}
												/>
											)}

										{user?.userType === USER_TYPES.COMPANY &&
										data?.userType === USER_TYPES.SEEKER ? (
											<></>
										) : (
											<>
												<div>
													<Button
														disabled={isBlock}
														onClick={
															user?.userType === USER_TYPES.SEEKER &&
															data?.userType === USER_TYPES.SEEKER
																? handleConnectRequest
																: followCompany
														}
														className="filled-btn rounded-pill lh-1"
													>
														{user?.userType === USER_TYPES.SEEKER &&
														data?.userType === USER_TYPES.SEEKER
															? buttonText
															: !followStatus
																? translate('Common.follow')
																: translate('Common.unFollow')}
													</Button>
												</div>
												{user?.userType === USER_TYPES.SEEKER &&
													data?.userType === USER_TYPES.SEEKER &&
													isConnected === 2 &&
													!connectRequestSentBySelf && (
														<div>
															<Button
																disabled={isBlock}
																onClick={() => handleRejectRequest()}
																className="filled-btn rounded-pill lh-1"
															>
																{translate('Button.reject')}
															</Button>
														</div>
													)}
											</>
										)}
										<div className="edit-action">
											<Dropdown>
												<Dropdown.Toggle
													variant="success"
													id="dropdown-basic"
													className="p-0 border-0 rounded-circle"
												>
													<i className="ri-more-2-fill"></i>
												</Dropdown.Toggle>

												<Dropdown.Menu>
													<Dropdown.Item
														onClick={() => {
															setModalConfig({
																visible: true,
																type: 'confirmModal',
																id: urlParams?.id,
																onClick: removeConnection,
																data: {
																	title: translate('Modal.wantToRemoveConnection'),
																},
																props: {
																	dialogClassName: 'confirm-modal',
																},
															});
														}}
													>
														<i className="ri-flag-line"></i> {translate('Report.removeConnection')}
													</Dropdown.Item>
													<Dropdown.Item onClick={reportProfile}>
														<i className="ri-flag-line"></i> {translate('Report.report')}
													</Dropdown.Item>
													{isBlock ? (
														<Dropdown.Item onClick={handleUnblockUser}>
															<i className="ri-spam-2-line"></i> {translate('Report.unblock')}
														</Dropdown.Item>
													) : (
														<Dropdown.Item onClick={handleBlockUserModal}>
															<i className="ri-spam-2-line"></i> {translate('Report.block')}
														</Dropdown.Item>
													)}
												</Dropdown.Menu>
											</Dropdown>
										</div>
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default OverviewBox;
